<template>
    <StaticSpinner v-if="!isEventsLoaded" />
    <div v-else-if="featuredMatches.length > 0" class="section">
        <div class="deco-player" />

        <Heading> {{ i18n.t('tournament_center_title_spotlight') }} </Heading>
        <div class="toolbar">
            <Navbar
                v-bind:isShowBottomRow="false"
                v-bind:isTransparent="true"
                v-bind:isFlush="true"
            >
                <template #topRow>
                    <NavbarMain>
                        <NavbarItem
                            v-for="(item, index) in featuredTabItems"
                            v-bind:key="item.id"
                            v-gtm:@click
                            v-gtm:label="`Match${index + 1}_Tab`"
                            v-bind:name="item.name"
                            v-bind:isActive="item.isActive"
                            v-bind:onclick="item.onClick"
                            v-bind:hasBottomRow="false"
                            v-bind:isActiveHasUnderline="true"
                        />
                    </NavbarMain>
                </template>
            </Navbar>
            <VButton
                v-gtm:@click
                v-gtm:label="'ViewAll'"
                v-on:click="navigateToMatches"
            >
                <template #content-left>
                    {{ i18n.t('fantasy_selection_view_all') }}
                </template>
                <template #icon>
                    <SboIcon name="system:arrowRight" right />
                </template>
            </VButton>
        </div>
        <template v-for="match in featuredMatches" v-bind:key="match.id">
            <FeaturedMatch
                v-if="featuredTab === match.id"
                v-gtm:type="'FeatureMatch'"
                v-bind:event="match"
            />
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, toRefs, watchEffect } from 'vue';
    import { useRouter } from 'vue-router';
    import SboIcon from '@/components/common/SboIcon.vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import Navbar from '@/components/common/navbar/Navbar.vue';
    import NavbarItem from '@/components/common/navbar/NavbarItem.vue';
    import NavbarMain from '@/components/common/navbar/NavbarMain.vue';
    import FeaturedMatch from '@/components/tournamentCenter/FeaturedMatch.vue';
    import Heading from '@/components/tournamentCenter/Heading.vue';
    import { i18n } from '@/core/lib/i18n';
    import { getTranslated } from '@/core/lib/translationHelper';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';

    const props = defineProps<{
        featuredMatches: ITournamentEvent[];
        isEventsLoaded: boolean;
    }>();

    const router = useRouter();

    const { featuredMatches, isEventsLoaded } = toRefs(props);

    function getMatchDisplayName(match: ITournamentEvent): string {
        const home = getTranslated(match.homeTeam?.name);
        const away = getTranslated(match.awayTeam?.name);
        return `${home} vs ${away}`;
    }

    const featuredTab = ref<number | null>(null);

    function setFeaturedTab(id: number) {
        featuredTab.value = id;
    }

    const featuredTabItems = computed(() => featuredMatches.value.map(match => ({
        id: match.id,
        name: getMatchDisplayName(match),
        isActive: featuredTab.value === match.id,
        onClick: () => setFeaturedTab(match.id),
    })));

    watchEffect(() => {
        if (((featuredTab.value === null || !featuredMatches.value.some(match => match.id === featuredTab.value)) && isEventsLoaded.value)) {
            featuredTab.value = featuredMatches.value[0].id;
        }
    });

    const navigateToMatches = () => {
        router.push({ name: 'TournamentMatches' });
    };
</script>

<style lang="scss" scoped>
@import "@/views/tournamentCenter/Overview";
</style>
